/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import {safeJSONParse} from '@ion/core/src/utils/JSONUtils';
import {
  initialPerTabState,
  PermanentTabs,
  selectedJobFacetsSignal,
  setSelectedJobFacetsSignal
} from './signals/state';

/**
 * Custom hook: useJobSelectedFacets
 *
 * Provides a set of utilities to manage selected job facets (filters) across different tabs.
 * It handles both permanent and dynamic tabs, allowing you to:
 * - Add or remove individual facet values.
 * - Clear all facets for a tab or a specific category.
 * - Check whether a tab, category, or value has active selections.
 * - Retrieve the current selected facets for a given tab.
 *
 * Returns:
 * {
 *   selectedJobFacetsSignal: the reactive signal holding all selected facets,
 *   getSelectedFacetsForTab: function to retrieve selected facets for a tab,
 *   updateFacetsForTab: function to add/remove a facet value,
 *   clearJobFacetsForTab: function to clear all facets for a tab,
 *   clearJobFacetCategory: function to remove a specific category from a tab,
 *   hasSelectedJobsFacets: function to check if a tab/category/value is selected
 * }
 *
 */
export const useJobSelectedFacets = () => {
  /**
   * Updates a single facet value for a given tab and category.
   * - If `isChecked` is true → adds the value.
   * - If `isChecked` is false → removes the value.
   * - Permanent tabs are never deleted; they are only cleared when empty.
   * - Dynamic tabs are deleted if no facets remain.
   */
  const updateFacetsForTab = (
    tab: string,
    facet: [string, string],
    isChecked: boolean
  ): void => {
    const current     = safeJSONParse(JSON.stringify(selectedJobFacetsSignal.value));
    const isPermanent = Object.values(PermanentTabs).includes(tab as PermanentTabs);
    const [facetCategory, facetValue] = facet;

    // Ensure the tab and category exist if adding
    if (isChecked) {
      if (!current[tab]) current[tab] = {};
      current[tab][facetCategory] = [facetValue];
    } else {
      // Removing facet value
      if (current[tab]?.[facetCategory]) {
        current[tab][facetCategory] = current[tab][facetCategory].filter(
          (v: string) => v !== facetValue
        );

        // If the category becomes empty, remove it
        if (current[tab][facetCategory].length === 0) {
          delete current[tab][facetCategory];
        }
      }

      // If the tab becomes empty, handle based on permanence
      if (Object.keys(current[tab] || {}).length === 0) {
        if (isPermanent) {
          current[tab] = {}; // keep but empty
        } else {
          delete current[tab]; // remove dynamic tab
        }
      }
    }
    setSelectedJobFacetsSignal(current);
  };

  /**
   * Clears all selected facets for a specific tab.
   * - Permanent tabs are retained but emptied.
   * - Dynamic tabs are removed entirely.
   */
  const clearJobFacetsForTab = (tab: PermanentTabs): void => {
    if (selectedJobFacetsSignal?.value) {
      const current = {...selectedJobFacetsSignal.value};
      const isPermanent = Object.values(PermanentTabs).includes(tab);
      if (isPermanent) {
        current[tab] = {};
      } else {
        delete current[tab];
      }
      setSelectedJobFacetsSignal(current);
    }
  };

  /**
   * Removes a specific category (facet) from a tab.
   * - Only the specified category is deleted, not the entire tab.
   * - Permanent tabs always retain their key, even if empty.
   * - Dynamic tabs remain but may end up empty (optional: could be removed if desired).
   */
  const clearJobFacetCategory = (tab: string, category: string) => {
    const current = {...selectedJobFacetsSignal.value};
    if (current[tab]) {
      const isPermanent = (Object.values(PermanentTabs) as string[]).includes(tab);
      delete current[tab][category];
      if (Object.keys(current[tab]).length === 0) {
        if (isPermanent) {
          current[tab] = {};
        }
      }
      setSelectedJobFacetsSignal(current);
    }
  };

  /**
   * Returns all selected facets for a given tab.
   * If the tab doesn’t exist, returns an empty object.
   */
  const getSelectedFacetsForTab = (tab: string) =>
    selectedJobFacetsSignal.value[tab] || {};

  /**
   * Checks whether a tab, category, or specific value within a category has active facets.
   * @param tab      The tab ID to check.
   * @param category (Optional) The category name to check within the tab.
   * @param value    (Optional) The facet value to verify within the category.
   * @returns true if the condition matches; false otherwise.
   */
  const hasSelectedJobsFacets = (
    tab: string,
    category?: string,
    value?: string
  ): boolean => {
    const tabFacets = selectedJobFacetsSignal.value[tab];
    if (!tabFacets) return false;

    // If category and value provided → check if that exact value exists
    if (category && value !== undefined) {
      const values = tabFacets[category];
      return Array.isArray(values) && values.includes(value);
    }

    // If only category provided → check if category has values
    if (category) {
      const values = tabFacets[category];
      return Array.isArray(values) && values.length > 0;
    }

    // If only tab provided → check if tab has any active values
    return Object.values(tabFacets).some(
      (values) => Array.isArray(values) && values.length > 0
    );
  };

  /**
   * Resets all facets to the initial state (permanent tabs cleared, dynamic tabs removed).
   * Call on module exit to ensure a clean state when re-entering the Jobs module.
   */
  const resetAllFacets = (): void => {
    setSelectedJobFacetsSignal(initialPerTabState(() => ({})));
  };

  return {
    selectedJobFacetsSignal,
    getSelectedFacetsForTab,
    updateFacetsForTab,
    clearJobFacetsForTab,
    clearJobFacetCategory,
    hasSelectedJobsFacets,
    resetAllFacets
  };
};

export default useJobSelectedFacets;
